// Reiter "Upload-Aufträge" der Deals-Liste: der laufende Auftrag und die
// letzten Erstellungen.
//
// Stand bis zum 23.09.2026 inline in VertragsAmPendingBoard.jsx, mit eigener
// Kopfzeile samt zweitem "Aktualisieren", Erklaerzeilen unter jeder
// Ueberschrift und der internen Batch-Kennung. Jetzt: Aktualisieren sitzt oben
// in der Liste, die Kennung nur noch im Tooltip.

import React from 'react';
import { T, karteStil, markeStil } from '../../lib/designTokens';

const STATUS = {
  completed: { text: 'Erfolgreich', farbe: T.gut },
  failed: { text: 'Fehler', farbe: T.gefahr },
  running: { text: 'Läuft', farbe: T.info },
  pending: { text: 'Wartet', farbe: T.muted },
};

function status(wert) {
  return STATUS[wert] || { text: wert || 'Unbekannt', farbe: T.muted };
}

function meldung(eintrag) {
  return String(eintrag?.message || eintrag?.error?.message || '').trim();
}

function OffenePlatzhalter({ liste, hoechstens }) {
  if (!Array.isArray(liste) || liste.length === 0) return null;
  return (
    <div style={{ color: T.warn, fontSize: T.tMikro, lineHeight: 1.45, marginTop: 3 }}>
      Offene Platzhalter: {liste.slice(0, hoechstens).join(', ')}{liste.length > hoechstens ? ' …' : ''}
    </div>
  );
}

const zelle = {
  padding: '9px 12px', fontSize: T.tSm, color: T.ink, textAlign: 'left',
  borderBottom: `1px solid ${T.hairline}`, verticalAlign: 'top',
};
const kopfZelle = {
  ...zelle, fontSize: T.tMikro, fontWeight: 700, color: T.muted,
  background: T.grund, borderBottom: `1px solid ${T.randWeich}`,
};
const ruhigerText = { fontSize: T.tText, color: T.muted, lineHeight: 1.6 };

function AktiverAuftrag({ batch }) {
  const jobs = Array.isArray(batch.jobs) ? batch.jobs : [];
  const gesamt = Number(batch.total || jobs.length || 0);
  const fertig = Number(batch.completed || 0);
  const fehler = Number(batch.failed || 0);
  const wartet = Number(batch.pending || 0);
  const laeuft = Number(batch.running || 0);
  const prozent = Math.max(0, Math.min(100, Number(batch.percent || 0)));
  const zahlen = [
    `${fertig}/${gesamt} fertig`,
    laeuft ? `${laeuft} läuft` : '',
    wartet ? `${wartet} wartet` : '',
    fehler ? `${fehler} Fehler` : '',
  ].filter(Boolean);

  return (
    <div style={{ ...karteStil, padding: '14px 16px', display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div title={batch.id ? `Auftrag ${batch.id}` : undefined}
          style={{ flex: 1, fontSize: T.tTitel, fontWeight: 700, color: T.navy }}>
          Upload läuft
        </div>
        <span style={{ fontSize: T.tMikro, color: T.body, fontWeight: 600 }}>{zahlen.join(' · ')}</span>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: T.hairline, overflow: 'hidden' }}>
        <div style={{
          width: `${prozent}%`, height: '100%', background: fehler > 0 ? T.orange : T.gut,
          transition: 'width 200ms ease',
        }} />
      </div>
      {jobs.length > 0 ? (
        <div style={{ display: 'grid', gap: 6 }}>
          {jobs.map((job) => {
            const s = status(job.status);
            return (
              <div key={job.id || `${job.dealId}-${job.status}`} style={{
                padding: '8px 10px', borderRadius: T.rChip, background: T.grund,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                  <div style={{ color: T.ink, fontSize: T.tSm, fontWeight: 700 }}>
                    Deal #{job.dealId || '—'} · {job.contractLabel || job.contractKey || 'Vertrag'}
                  </div>
                  <span style={{ color: s.farbe, fontSize: T.tMikro, fontWeight: 700 }}>{s.text}</span>
                </div>
                {meldung(job) ? (
                  <div style={{ color: s.farbe, fontSize: T.tMikro, lineHeight: 1.45, marginTop: 3 }}>{meldung(job)}</div>
                ) : null}
                <OffenePlatzhalter liste={job.unresolved} hoechstens={8} />
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function Historie({ historie }) {
  if (historie.length === 0) {
    return <div style={ruhigerText}>Noch nichts erstellt.</div>;
  }
  return (
    <div style={{ border: `1px solid ${T.randWeich}`, borderRadius: T.rKarte, overflowX: 'auto', background: T.flaeche }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
        <thead>
          <tr>
            <th style={kopfZelle}>Zeit</th>
            <th style={kopfZelle}>Deal</th>
            <th style={kopfZelle}>Vertrag</th>
            <th style={kopfZelle}>Status</th>
            <th style={kopfZelle}>Hinweis</th>
          </tr>
        </thead>
        <tbody>
          {historie.map((eintrag) => {
            const s = status(eintrag.status);
            return (
              <tr key={eintrag.id || `${eintrag.dealId}-${eintrag.updatedAt}`}>
                <td style={{ ...zelle, fontSize: T.tMikro, color: T.muted, whiteSpace: 'nowrap' }}>
                  {eintrag.updatedAt ? new Date(eintrag.updatedAt).toLocaleString('de-DE', {
                    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                  }) : '—'}
                </td>
                <td style={{ ...zelle, fontWeight: 700 }}>#{eintrag.dealId || '—'}</td>
                <td style={zelle}>{eintrag.contractLabel || eintrag.contractKey || 'Vertrag'}</td>
                <td style={zelle}>
                  <span style={markeStil(s.farbe, T.randWeich, T.flaeche)}>{s.text}</span>
                </td>
                <td style={{ ...zelle, fontSize: T.tMikro, color: T.body, lineHeight: 1.45 }}>
                  {meldung(eintrag) || (eintrag.status === 'completed' ? 'PDF und HTML am Deal, Phase gesetzt.' : '—')}
                  <OffenePlatzhalter liste={eintrag.unresolved} hoechstens={10} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * batch: der laufende Auftrag oder null; historie: die letzten Erstellungen;
 * fehler: Meldung, wenn die Liste nicht geladen werden konnte;
 * hinweis: der Abruf scheiterte nur vorübergehend (Ablage überlastet) - ruhig
 * angezeigt, denn am Upload selbst ändert das nichts (seit 25.10.2026).
 */
export default function UploadAuftraege({ batch, historie, fehler, hinweis }) {
  return (
    <div style={{ padding: 16, display: 'grid', gap: 16 }}>
      {fehler ? (
        <div style={{
          padding: '9px 12px', borderRadius: T.rChip, background: T.gefahrGrund,
          borderLeft: `3px solid ${T.gefahr}`, color: T.gefahr, fontSize: T.tMeta, fontWeight: 600,
        }}>{fehler}</div>
      ) : null}
      {hinweis && !fehler ? (
        <div style={{
          padding: '9px 12px', borderRadius: T.rChip, background: T.grund,
          borderLeft: `3px solid ${T.rand}`, color: T.muted, fontSize: T.tMeta,
        }}>{hinweis}</div>
      ) : null}

      {batch ? <AktiverAuftrag batch={batch} /> : <div style={ruhigerText}>Gerade läuft kein Upload.</div>}

      <div style={{ display: 'grid', gap: 8 }}>
        <div style={{ fontSize: T.tTitel, fontWeight: 700, color: T.navy }}>Zuletzt erstellt</div>
        <Historie historie={Array.isArray(historie) ? historie : []} />
      </div>
    </div>
  );
}
